import React, { useState } from 'react';

// About is organized like a product site: the Product tab is the
// description (what it is, why it matters); the Technical Specifications
// tab is the spec sheet an engineer checks before connecting an SBC.

// What is actually deployed, stamped at build time.
//
// Both come from the build rather than from a constant kept here: the version
// is npm's own `npm_package_version`, so package.json is the single place it
// is written, and the date is the moment the bundle was produced. A version
// maintained by hand in the source is a version that is wrong the first time
// someone forgets — and this line exists precisely so a reader can tell
// whether the page in front of them is the current one. That question came up
// for real: a correctly deployed console read as stale because the browser was
// serving a cached index.html, and there was nothing on the page to say which
// build it was.
//
// The dev server sets neither, which is why the fallback says so plainly
// instead of inventing a number.
const APP_VERSION = process.env.REACT_APP_VERSION || null;
const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || null;

const BuildStamp: React.FC = () => (
  <p className="about-build-stamp">
    {APP_VERSION
      ? <>Console version <strong>{APP_VERSION}</strong></>
      : <>Development build</>}
    {BUILD_DATE && <> · built {BUILD_DATE}</>}
  </p>
);

const ProductSection: React.FC = () => (
  <>
    <section>
      <h3>Governance Control Plane</h3>

      <p>
        The 12by9 Governance Control Plane is a complete, self-hosted system
        for governing recorded conversations: who consented to what, who has
        touched a call since, and when it has to be disposed of. Consent is
        recorded per participant and per purpose and travels inside the
        conversation record itself; every access is written to a
        tamper-evident ledger; and retention, redaction, and revocation are
        enforced by policy rather than by hand. "Do we have consent, who has
        had this call, and is it still supposed to exist?" are questions with
        one-click answers here, each backed by evidence you can hand an
        auditor.
      </p>

      <p>
        That governance sits on a recording platform of its own rather than a
        layer bolted onto someone else's. Point your SBC or call platform at
        it, and every call it hands over is captured, indexed, and ready to
        replay from this console within seconds of hanging up — with each
        participant's consent, the audit trail, and the retention clock
        already attached.
      </p>

      <p>
        At its core is a native SIPREC recording engine that speaks the
        standard your session border controller already knows (RFC 7865/7866),
        over plain or TLS-secured SIP, with SRTP decryption for encrypted
        media. It handles the codecs found in real-world telephony — G.711,
        G.722, and Opus — and resolves each one from the RTP stream itself, so
        a call whose legs negotiate different codecs, or that switches codec
        part-way through, still records cleanly. Every participant lands on
        their own channel on a single shared timeline, so you can always hear
        who said what, and when. Alongside the WAV
        recording, each call is preserved as a vCon: an open, portable JSON
        record that bundles the participants, timing, SIPREC metadata, and the
        call audio itself into a single self-contained document.
      </p>

      <p>
        The recording engine doesn't just capture audio — it understands the
        call. Caller and callee identities, recording group lineage, and
        transfer history are extracted from the SIPREC metadata and stored
        with every session, which is what makes the archive genuinely
        searchable: find calls by who was on them, when they happened, or
        which multi-leg conversation they belong to, then listen right in the
        browser or open the raw signaling metadata when you need to dig
        deeper.
      </p>

      <p>
        The platform is built to sit safely on the open internet. SIP and RTP
        are default-deny — only traffic from sources you have explicitly
        allowlisted is accepted, and everything else is logged with
        geolocation so you can watch who is knocking. The console itself is
        authenticated, with per-user login IP restrictions and a full audit
        trail, and the operations pages give you live visibility into the
        recorder's health, host resources, and storage — down to the source
        address of each active call's SIP signaling and received media
        streams — including the ability to restart the recorder from your
        browser, or start it again after a host reboot has left it down.
      </p>

      <p>
        Conversations aren't just archived — they're accessible to your other
        systems. A built-in MCP (Model Context Protocol) server lets AI agents
        and external applications search the archive, retrieve complete
        conversation records, and contribute their own — say, appending a
        transcript or summary back onto a call — each under its own API key
        with individually granted privileges, optional source-IP restrictions,
        and full accounting of every use and every rejected attempt. People
        can connect too, with their own console sign-in rather than a key: add
        the server once as a connector on claude.ai and Claude can search your
        conversations from an iPad, the web or the desktop app, seeing only
        what that person's account may see. In Claude Code it is a single
        command. And because everything is standard WAV and open vCon JSON,
        your data is never locked in.
      </p>

      <p>
        And because recorded conversations are regulated data, the platform
        keeps evidence, not just files. Every vCon carries a tamper-evident
        lifecycle ledger — created, received, enriched, handed to an external
        application, deleted — following the emerging IETF vCon lifecycle
        standard. Each entry is cryptographically chained to the one before
        it, so any after-the-fact alteration is detectable at the press of a
        verify button, and the trail survives even when the conversation
        itself is deleted. When an auditor asks "who has had access to this
        call?", the answer is one click, with names, applications, IP
        addresses, and timestamps.
      </p>

      <p>
        Consent is a first-class citizen of that same story. Following the
        IETF vCon lawful-basis model, each participant's permission is recorded
        inside the conversation record itself, where it travels with the data:
        which purposes they allow — recording, transcription, analysis, AI
        training — and, just as importantly, <em>why</em> that processing is
        lawful at all. That second question has six possible answers under
        GDPR Article 6, and two people on the same call routinely have
        different ones: an agent covered by their employment contract, a
        customer who simply said yes. The platform records the grounds per
        person rather than assuming everyone consented. Nothing is ever
        assumed: a recording starts with no permission asserted until someone
        explicitly records it, from the console or via an integrated
        application. And when a participant revokes, the platform acts on it
        immediately — external applications lose access to that conversation
        at the next request, anything the platform had derived from their
        words is destroyed, reviewers see the revocation warning, and both the
        grant and the revocation stand in the ledger with the rest of the
        conversation's history.
      </p>

      <p>
        At scale, consent runs on policy rather than clicks. The consent
        management console maintains a registry of every participant the
        platform has seen, each carrying the purposes they've agreed to; when
        a new call is recorded, each participant's consent is stamped from
        the registry automatically, newcomers are enrolled under the default
        policy for their domain, and anyone from an unconfigured domain falls
        back to the most conservative default — recording only. Set the
        policy once per customer domain, and every conversation after that
        is born with the right consent already attached. And consent here
        isn't just a checkbox — every assertion carries both the grounds it
        rests on and proof of how it was acquired, from a signed agreement
        reference to the announcement in the recording itself to a portal
        receipt. The two are kept apart deliberately, because they answer
        different questions: an MSA is evidence of a contract and a portal
        receipt is evidence of consent, and a record that says only "we have
        evidence" cannot say what it is evidence of. So "do we have consent?"
        always comes with "on what basis, and here's the evidence."
      </p>

      <p>
        Best of all, the participants themselves have a voice. The
        data-subject consent portal gives any party a private, passwordless
        link — no account required — where they read the agreement, choose
        exactly which purposes they consent to, apply their decision to
        their existing conversations, and walk away with a cryptographically
        signed receipt they can independently verify. Consent a person
        signed for themselves outranks any operator default, is marked as
        such across the platform, and their withdrawal takes effect at the
        very next request.
      </p>

      <p>
        And a person is one person, however they reach you. The same customer
        calls from a desk phone and a mobile, emails from two addresses and
        chats from a third, and the platform knows all of those are them: one
        record, with every address it answers to, and one consent decision
        that covers every channel. Their addresses are joined only on proof —
        their own NetSapiens sign-in, a one-time code sent to the address, or
        an administrator who writes down why — and a phone number that merely
        matches is shown as a possible match, never joined on its own, because
        numbers get reassigned. How far a yes travels depends on how sure the
        platform is that it is really them: a refusal is honoured wherever
        their address appears, but a grant is honoured only on a channel that
        proves who is there, so a spoofed caller ID can never borrow someone
        else's consent. A main line or a shared support inbox is marked as
        such and never carries one person's yes for everyone behind it.
      </p>

      <p>
        Chat joins calls and email. In 12by9's own chat portal an administrator
        chooses which chats to archive; a recorder everyone in the chat can see
        joins it, and each conversation — closed after six hours of silence,
        or a day after it began if it never pauses —
        arrives here as a record like any call: searchable, threaded with the
        rest of the chat, and governed by the same consent and retention.
        Chats among your own people are kept now. A chat with an outside
        contact on WhatsApp, Messenger or Google Chat is held, not kept, until
        holding outside people's messages has been cleared. When the platform
        is set to take consent from outside contacts, the person whose chat it
        is can ask them from the portal's Archive tab: the request goes out
        from their own account, with a link to this platform's consent page.
        They choose recording (which keeps the chat) and, separately,
        analysis (which lets Ask and Analytics read it); AI training is not
        asked for. A contact who agrees sends back the short code that page
        gives them,
        which shows the answer came from them and not from whoever a link was
        forwarded to. When the code arrives the platform signs a proof
        receipt, beside the consent receipt, naming that receipt, the chat
        account and the message that carried the code, so one signed document
        ties the agreement to the account. Their conversations are then kept, from the moment they
        agreed or since archiving began if the request asked for that. A no,
        or a request left to expire after a week, deletes what was held for
        them. Everyone outside a chat must agree before any of it is kept.
        A contact who is
        really one of your own people — their own Google Chat or WhatsApp
        account — counts as internal once that is proven: by their account in
        the portal being signed in to that network as the contact, or by an
        administrator's link with a reason. Their messages are then kept and
        filed under that person, with the proof in the identity ledger.
      </p>

      <p>
        What was recorded doesn't stay forever — it lives out a governed
        disposition. Retention policy, set per customer domain with a global
        default, expires conversations automatically once their time is up:
        the recording and its vCon are permanently deleted while the ledger
        keeps the tombstone, so you can always prove both that a call
        existed and that it was disposed of on schedule. A participant can
        be redacted from a conversation without destroying it — their name
        and number are replaced by a marker and their side of the audio is
        silenced, while the other party and the audit trail remain intact.
        That comes two ways, because it answers two different requests.
        Erasing acts on the conversation itself, in the archive and the source
        recording alike, and cannot be undone: it is the answer to someone
        exercising their right to erasure. Deriving instead leaves the
        original untouched and produces a second, redacted conversation that
        points back at it — the original becomes restricted, applications are
        served the redacted copy, and nothing is destroyed. That is what a
        person who declined one purpose while the others agreed actually
        needs, and it can be undone if they change their mind. And revocation
        reaches beyond the platform itself: because the ledger knows exactly
        which applications took copies of a conversation, revoking consent
        automatically serves each of them a purge notice; they confirm over
        the same API, and the confirmation lands in the ledger. "Who still
        holds this conversation, and have they let go of it?" is answered on
        the same screen as the consent itself.
      </p>

      <p>
        A conversation rarely stays in one channel. Someone calls, the agent
        texts them a link, and the thing is settled by email three days later —
        one interaction, three records. Those are linked into a thread, and the
        link lives inside the conversations themselves rather than only in a
        database, so it survives export to anywhere else. Where the recording
        client itself said two records are one call — the segments of a
        transfer, and the separate session a supervised transfer opens for the
        consultation — they are linked without anyone being asked, because that
        is writing down an assertion someone else made rather than forming one.
        A six-segment transfer chain reassembles itself. Where the platform can
        only infer, it suggests: conversations sharing a participant within a
        few days, for a person to confirm, because no heuristic can tell a
        follow-up from two people who simply spoke twice.
        And a thread is not a shortcut around consent — asking anything of a
        whole thread is governed by its most restrictive member, so joining a
        conversation to others can never widen what was permitted on any of
        them.
      </p>

      <p>
        Governed recording also meets people where they already work. On
        platforms running NetSapiens Horizon, this console embeds inside it —
        recordings, transcripts and consent as pages of the operator's own
        portal — and reaches the rows of Horizon's own call log: a call that
        was recorded carries a play button and a link to its conversation
        record, right where someone had already gone looking for that call. A
        call transferred between people was recorded in several segments, and
        the buttons offer them in order, with who was on each and how long it
        ran, instead of making anyone guess which one was meant. A row whose
        recording the reader may not hear carries nothing at all — that is
        answered by the platform under their own role, not decided by the page.
        The same is true of Horizon's list of users: beside each person is
        what has been recorded about their consent — which purposes they
        permit, how that was obtained, and the signed receipt standing behind
        it, which can be read and re-checked without leaving the page. That
        view never changes a decision, on purpose. Changing someone's consent
        rewrites every conversation they appear in, so it stays on the page
        built to ask the question properly, rather than sitting beside Delete
        on a page meant for bulk administration. Where there is nothing
        recorded at all — which is most people, until their first recorded
        call — the row offers the one thing that legitimately fills that gap:
        it asks them. A link is created for that person, they open it and
        decide for themselves, and what comes back is their own decision with
        their own receipt, not one an administrator entered on their behalf.
        Asking twice hands back the same link rather than putting two of them
        in circulation, and cancelling stops it working at once.
      </p>

      <p>
        Once conversations are transcribed they can also be asked about in
        plain English. The Ask page takes a question — "what do people call
        about?", "how many conversations mentioned the renewal last month?",
        "who has been in touch most, and about what?" — and answers it from
        the conversations in the territory you have selected, citing the ones
        it used so any claim can be opened and read. It is deliberately not a
        chatbot with an opinion: it can only search, count and read, it shows
        every search it ran on the way to an answer, and it says when it could
        not see something. A conversation nobody has transcribed, or one a
        party has not consented to have analysed, is reported as withheld
        rather than quietly left out — because an answer that silently omits
        half the evidence is worse than no answer at all. A host can also
        answer with a language model running on its own hardware instead of
        Google's, so the conversations it reads never leave the machine — the
        same questions, the same consent rules, slower answers and no bill.
        Where a host has both — or several local models — you choose for each
        question, and every answer says which one wrote it.
      </p>

      <p>
        That feature is governed like everything else here. Reading a
        conversation's words requires that party's consent to the analysis
        purpose specifically — consenting to be transcribed is not consenting
        to be reasoned over — and each disclosure is written to the same
        ledger as any other, naming which conversation went where. Whole
        bodies of recordings can be closed to it entirely, the permission is
        granted per account rather than assumed, and every question's cost is
        recorded, so "what is this costing and what has it read" has an answer
        on one page.
      </p>

      <p>
        The same questions can be asked by software rather than by a person:
        an application you have registered can put one to the platform and get
        the answer back, without being handed the conversations to work
        through itself. It sees only what its own key was scoped to, it is
        metered separately, and it is held to a stricter line than a person
        is — a member of your staff may decide to read a conversation that
        nobody has consented to have analysed, and answer for that decision;
        an application simply cannot. The log shows which of the two asked, so
        that difference is visible afterwards rather than only intended.
      </p>

      <p>
        The same conversations also answer the operational question a service
        manager asks first: how are these calls going? The Analytics page reads
        handle time, hold and transfer language, how calls end, and what they
        are actually about, and cuts it by industry — classified from what was
        said on the call rather than from whatever the domain happens to be
        called, because the two disagree more often than anyone expects. It is
        equally explicit about its own limits: the page opens with what it
        measures, what it can only infer from language, and what it cannot see
        at all, so nobody manages to a number that was never being measured. A
        satisfaction figure derived from please-and-thank-you is labelled a
        proxy on the page that shows it, and a measure that would need survey
        scores, agent identities or queue events the platform does not hold is
        listed as unavailable rather than quietly approximated.
      </p>

      <p>
        The whole platform — recording engine, API, and this console — runs on
        a single Linux VM or Mac with a MariaDB database, deploys from source
        in minutes, and is operated entirely from the pages you're looking at.
        For protocols, codecs, and formats, see the Technical Specifications
        tab.
      </p>
    </section>

    <section>
      <h3>12by9 Corp</h3>
      <p>
        The Governance Control Plane is built and maintained by 12by9 Corp.
      </p>
      <p>
        {/* TODO: company background, contact information, and support channels. */}
        More about 12by9 Corp coming soon.
      </p>
    </section>
  </>
);

const SpecsSection: React.FC = () => (
  <section>
    <h3>Technical Specifications</h3>

    <h4>Recording engine</h4>
    <ul>
      <li>
        SIPREC Session Recording Server (RFC 7865/7866): terminates recording
        sessions over SIP with TCP and TLS transports, answering as the SRS
        with per-call RTP port allocation.
      </li>
      <li>
        Media handling: RTP and SRTP (SDES key exchange; AES-CM 128/256 crypto
        suites), with G.711 µ-law and A-law, G.722 (ITU-T sub-band ADPCM at 64,
        56 and 48 kbit/s), and Opus decoding, and automatic resampling to the
        configured output rate.
      </li>
      <li>
        Per-stream codec resolution: each recorded stream is decoded according
        to its own m-line's payload types and each packet's RTP payload type,
        so legs that negotiate different codecs — or that change codec during
        the call — are each decoded correctly rather than following whichever
        codec the session offered first.
      </li>
      <li>
        Dual-stream capture: each participant on its own channel (stereo) or
        mixed to mono; WAV output in µ-law, A-law, or linear PCM. All of a
        call's streams share one buffer and one timeline, so channels stay
        aligned with each other whatever mix of codecs the legs use.
      </li>
      <li>
        Multi-party barge-in recording: when additional parties monitor or
        barge into a call, the recorder captures every stream — one WAV channel
        per party (multi-channel output beyond the usual stereo) — and lists all
        participants, with their roles, in the vCon, DB, and consent stamps.
      </li>
      <li>
        vCon generation: every completed call can also produce a vCon (IETF
        conversation container) with the parties, timing, SIPREC metadata, and
        the full call audio embedded — a single portable JSON record of the
        conversation. Each vCon's creation is entered into the lifecycle
        ledger, and each party's consent is stamped from the party consent
        registry at the same moment.
      </li>
      <li>
        Rich session metadata from the SIPREC rs-metadata XML (including
        NetSapiens extensions): calling/called party, barge-in parties and their
        roles, recording group ID and sequence, transfer tracking across call
        legs.
      </li>
      <li>
        Operational resilience: abandoned-call cleanup, graceful shutdown with
        finalized recordings, a 10-second status heartbeat, console-initiated
        restart, and console-initiated cold start of a stopped recorder on
        either platform — the console asks by writing a request the host's own
        service manager acts on, so bringing the recorder back never means
        letting the web server run commands.
      </li>
    </ul>

    <h4>Web console</h4>
    <ul>
      <li>
        Status: live recorder health (SIP/TLS listeners, active recordings,
        RTP ports in use), per-recording SIP invite source and observed RTP
        media source IP:ports, host CPU/memory/network, recording storage
        broken out as WAV vs vCon and by volume, how many vCons are held
        locally versus in Cloud Storage, database status, and controls to
        restart the recorder — or start it again while it is down, on a host
        set up to allow it, and saying so plainly where it is not.
      </li>
      <li>
        Recording Sessions: searchable session archive with time-range
        filters, per-session participants, SIPREC group lineage, raw
        rs-metadata viewer, and in-browser playback.
      </li>
      <li>
        VCons: the vCon archive with search (UUID, parties, group, time
        range), playback of the audio embedded in the vCon itself, download,
        upload, confirmed delete, per-party redaction in either mode, a per-vCon lifecycle
        timeline, and consent management — per-party, per-purpose
        grant/revoke with status badges on every row and a copies &amp;
        notifications view showing which applications hold a copy and where
        each stands in the revocation cascade.
      </li>
      <li>
        Consent: the consent authority page — a party consent registry
        (pre-populatable from the existing archive, auto-enrolling as new
        parties appear) and per-domain default purposes with a protected
        recording-only fallback, applied automatically to every new vCon.
      </li>
      <li>
        People: every person (or shared line or mailbox) with every address it
        answers to — seat, SIP devices, phone numbers, email, chat — how well
        each is proven, the consent that covers them all, their conversations
        and their history. Territory administrators merge records that are one
        person, split an address back out, mark an address shared, rename a
        record and set whether it is a person, a role or a service, each with a written
        reason; records sharing only a phone number are offered as possible
        matches and never joined automatically.
      </li>
      <li>
        Territories: what each territory and domain actually holds — parties,
        vCons and bytes, consent records, recorded sessions, accounts, and when
        an MCP key last read from it — with every domain listed against its
        territory's subtotal.
      </li>
      <li>
        Settings: console user accounts with an access level and the
        territory/domain they are bound to, per-IP login restrictions, the
        SIP/RTP source-IP allowlist, the MCP application registry (each key
        scoped to a territory and domain, with an optional source-IP allowlist
        whose entries are validated as IPs/CIDRs — left empty, the key works
        from any IP), and retention policy (global default plus per-domain
        overrides, with dry-run preview and manual disposition runs).
      </li>
      <li>
        Logs: login audit trail, blocked SIP/RTP attempts and blocked MCP
        requests (both with geolocation and a per-country world heat map),
        the MCP access log with its query-shape analysis, and the vCon
        Lifecycle Ledger with on-demand chain verification.
      </li>
    </ul>

    <h4>Service analytics</h4>
    <ul>
      <li>
        Derived entirely from what the platform already stores — transcript
        text and audio duration. Measured: handle-time distribution (median,
        p90, banded), talk density in words per minute of audio, hold and
        transfer language, call-recording-disclosure rate, topic mix, and
        per-call ASR confidence. Inferred as labelled proxies: satisfaction
        (gratitude against frustration vocabulary), resolution (close-out
        against deferral language), empathy, and repeat contact. Declared
        unavailable, with the reason: escalation on PII-redacted material
        (job titles are redacted to{' '}
        <span className="monospace">[occupation]</span>), agent-level
        performance (no speaker diarization), trends (placeholder timestamps),
        revenue (redacted amounts), and queue time, abandon rate or occupancy
        (no telephony events).
      </li>
      <li>
        Gated on the per-party analysis consent purpose, before a single word
        is scored. Deriving frustration, apology and resolution signals from
        what someone said is analysis whatever the mechanism, so the batch job
        checks the same grant the Ask tab does and skips a conversation whose
        parties have not all given it, reporting how many it refused rather
        than quietly narrowing the corpus. Imported research material with no
        participants indexed has no data subject to ask and is reported
        separately as exempt — what governs it is its licence, not a grant
        nobody could give.
      </li>
      <li>
        Withdrawing analysis destroys what was derived under it. A revocation
        removes that conversation's stored scores along with notifying every
        external holder, rather than leaving the words still counted in every
        figure on the page under a different name.
      </li>
      <li>
        Industry is classified from each conversation's own vocabulary rather
        than from its domain. On the research corpus the two disagree badly —
        the domain named{' '}
        <span className="monospace">medicare_inbound</span> holds 64% of the
        corpus and is 28% pet boarding — so the page shows the label-versus-
        content breakdown alongside the figures rather than asking anyone to
        take the cut on trust.
      </li>
      <li>
        Live, and scoped like every other page. The analysis itself is a batch
        job rather than a query: each conversation is measured once from the
        text already extracted for search, and the page then aggregates
        indexed columns, so a hundred thousand conversations render as a page
        load rather than a report run. Byte-identical duplicates — which
        imported material does contain — are collapsed before any rate is
        computed, and a conversation whose duration is not yet established is
        excluded from time figures rather than counted as zero, which would
        shorten every average on the page.
      </li>
      <li>
        Where a figure is missing the page says which, and why. A scope with
        no analysed conversations renders the same board with an empty state
        and the list of measures that would appear, rather than an error; a
        scope where nothing records call direction reports the inbound share
        as unknown instead of as zero.
      </li>
    </ul>

    <h4>Territory and domain</h4>
    <ul>
      <li>
        Everything the platform holds — recordings, vCons, consent records,
        console accounts and MCP API keys — belongs to exactly one{' '}
        <strong>domain</strong>, and every domain to one{' '}
        <strong>territory</strong>. A vCon's place is derived when it is
        indexed, from the host of the party that owns the conversation, so a
        new PBX or email domain appears in the console the first time traffic
        arrives rather than having to be registered in advance. A domain is
        named for its host, so what the console shows always matches what is in
        the addresses; the short brand name lives on the territory above it. An
        alias table is available for the case where a host must map to a
        differently-named domain.
      </li>
      <li>
        Console accounts come at three levels. A{' '}
        <strong>Super User</strong> sees every territory; a{' '}
        <strong>territory</strong> account sees one territory and all the
        domains beneath it; a <strong>domain</strong> account sees a single
        domain. The level is enforced on the server for every listing, and a
        request that reaches outside an account's scope is refused outright
        rather than answered with an empty page — so a filter can narrow what
        an account sees, never widen it.
      </li>
      <li>
        MCP API keys carry the same two-level scope, each accepting{' '}
        <span className="monospace">*</span> for "all". The key's scope is an
        isolation boundary, not a default: a key bound to one territory cannot
        resolve a vCon in another even when it asks by exact UUID or filename.
      </li>
      <li>
        A territory can be marked read-only, which refuses every write to it
        from every key regardless of privilege — the constraint belongs to the
        data's licence rather than to a permission level. Territories are also
        marked according to whether their vCons come from the recorder, which
        is how the console knows when a SIPREC session or consent record exists
        to join to.
      </li>
    </ul>

    <h4>Security</h4>
    <ul>
      <li>
        Default-deny network posture: SIP and RTP traffic is only accepted
        from allowlisted source IPs/CIDRs, refreshed live from the database;
        rejected sources are logged with geolocation.
      </li>
      <li>TLS for SIP signaling and SRTP for media confidentiality.</li>
      <li>
        Authenticated console with per-user allowed-login IPs and a full login
        audit log.
      </li>
      <li>
        Sign-in rate limiting on two independent thresholds — per account and,
        more loosely, per source IP, so a spray across many accounts is caught
        even though no single account exceeds its own limit. Checked before the
        password hash is computed, since that work is deliberately expensive.
        A successful sign-in clears the count, and throttled attempts are
        audited alongside every other outcome.
      </li>
      <li>
        Per-application MCP API keys with individual privilege grants
        (query / download / upload / update / consent / lifecycle), optional
        source-IP restrictions, instant rotation, and an unauthorized-attempt
        log capturing the presented credential prefix, attempted operation,
        and client fingerprint. The same page makes receiver keys: a key that
        ingests chat or email, set apart from the read privileges because a
        receiver should hold nothing else. A receiver must name one territory
        and one domain, since everything it stores is filed there, and can
        name one that does not exist yet, so a new organisation's place
        appears as soon as its key is saved. An email receiver also has the
        address its colleagues forward to, and says whose mail it accepts as
        internal, defaulting to its own domain. Ticking a receive capability
        clears the read privileges, since a receiver should hold nothing
        else. The Cloudflare Worker's relay key is made here too, and may
        hold nothing but relaying.
      </li>
      <li>
        Person identity: a person owns typed identifiers — NetSapiens seat,
        SIP AOR, E.164 phone, mailbox, Matrix account, and the bridged chat
        networks' ids — each normalised by one set of rules implemented twice
        (PHP and the C++ recorder) and held to one shared file of test vectors,
        each with an assurance level (seen, attested by our own PBX or
        homeserver, proven by the person, attested by an administrator) and a
        validity window, so a reassigned number is closed rather than edited.
        Resolution is exact, never fuzzy and never on names. The uid a SIPREC
        client asserts is who was called and the AOR the device that answered,
        so a queue answered on an agent's handset stays the queue. Proof comes
        from Horizon sign-in (the HMAC-verified callback proves the seat, with
        nothing sent) or a one-time code — six digits, fifteen minutes, five
        attempts, rate-limited per address and per person, only its hash
        stored — sent by email or to a chat account on the platform's own
        homeserver, to an address the person typed themselves; each channel
        stays off until its sender is configured. Linking a new address needs
        an already-proven person, so a forwarded consent link cannot attach a
        stranger's address. A merge takes the most restrictive consent of the
        two until the person decides again, and their own decision then covers
        every address they own. A rename changes only the name shown, never an
        address or consent, and a stored conversation keeps the name it was
        written with. Every link, proof, merge, split, rename and change is
        written to a separate hash-chained person ledger, attributed and with
        its reason.
      </li>
      <li>
        Consent decision at capture: one platform setting, read by the
        recorder and the API alike, chooses whether the consent stamped on a
        new conversation comes from the address-keyed registry or from the
        person. Under the person, a refusal applies wherever any of their
        addresses is matched, and a grant only when this conversation's
        identifier meets its channel's bar — an on-net seat; for email,
        internal Workspace mail (every party internal), a DMARC pass, or a
        proven address; for chat, an internal login or a proven identity.
        Off-net callers' grants do not travel: that bar needs STIR/SHAKEN
        attestation, which never reaches a SIPREC recorder. Below the bar the
        party gets the domain default minus their refusals, and a shared
        identifier is always below it. The lifecycle ledger records which path
        decided each stamp and why, and a read-only report shows every party
        whose stamp a switch would change before it is made.
      </li>
      <li>
        Consent management (per draft-howe-vcon-lawful-basis): each party's
        consent is stored as an attachment inside the vCon itself — a named
        lawful basis with a grant per purpose (recording, transcription,
        analysis, ai_training), timestamps and asserting identity — and
        indexed for search. Each participant's uuid (core §4.2.10, the identity
        the standard registers for correlation across interactions) is the
        PERSON — the same value on that person's calls, email and chat —
        while uid carries the NetSapiens account the recording client asserted.
        Documents written between 4 Sep and Oct 2026 carried the account in
        uuid too; readers tell the two apart by shape.
        Attachments are written carrying both that shape
        and the one this platform started with (the expired
        draft-howe-vcon-consent), so documents already stored stay readable
        while readers migrate; either shape is accepted on the way in, which
        is what lets a vCon produced by another implementation arrive with its
        consent intact. No consent is ever assumed, and what that absence MEANS
        is a recorded decision rather than an accident of implementation. Under
        the consent basis it is a refusal: an application reaching the API is a
        Data Processor and is refused a conversation whose parties have not all
        permitted it. Under legitimate interest it is not, because that basis is
        opt-out by construction and denying on silence would make it stricter
        than consent. The console is the Data Controller and keeps the softer
        rule — it warns where the API refuses, because answering a subject
        access request about a conversation means being able to open it. A
        revocation blocks everywhere, on every basis, and is ledgered like every
        grant.
      </li>
      <li>
        Redaction answers two different questions and now has two modes.
        Erasing a party rewrites the conversation and its source recording in
        place and cannot be undone — the right answer to an erasure request and
        to nothing else. Deriving instead leaves the original untouched and
        writes a second conversation with that party's identity and voice
        removed, which references the first as the version it was redacted
        from; the original then becomes restricted, and applications asking for
        it are pointed at the redacted copy. That is what a consent carve-out
        needs: a party who declines one purpose while the others agree has not
        asked to be erased, and if they change their mind there has to be
        something left to restore. In both modes, files the party attached to
        an email lose their bytes but keep their name, size and digest, since
        a PDF cannot be scrubbed the way text can. Files other parties sent
        are left whole, even where they mention the redacted party.
      </li>
      <li>
        Lawful basis, recorded per party: which of GDPR Article 6's six
        grounds a party's processing rests on — consent, contract, legal
        obligation, vital interests, public task or legitimate interests —
        held on the registry entry, on the domain's standing policy, and on
        every grant, and carried inside the vCon's own attachment. Two parties
        to one conversation can rest on different grounds, which is the normal
        case rather than an edge one: an agent under an employment contract
        and a customer under consent. A console grant leaves a party on
        whatever basis it already has unless the basis is changed explicitly,
        since ticking a purpose box is not what establishes the grounds.
      </li>
      <li>
        Proof of acquisition: separately from the basis, every consent
        assertion — and every domain policy and registry entry — can carry
        typed evidence of how that basis was established (a-priori agreement,
        call announcement, DTMF, verbal, portal receipt) with a reference to
        the artifact; domain-level evidence and basis are inherited together
        by auto-enrolled parties and travel inside the vCon's consent
        attachment. Basis and proof answer different questions — why the
        processing is lawful, and what can be shown for it — so an MSA is
        evidence of a contract, and a portal receipt evidence of consent.
      </li>
      <li>
        Data-subject consent portal: passwordless, single-token invite links
        (created from the console or via the request_consent MCP tool) lead
        to a public portal where the party grants or revokes per purpose,
        optionally across their existing conversations, and receives an
        Ed25519-signed, independently verifiable consent receipt
        (JLINC/SISA-aligned). Portal-asserted consent is the strongest
        registry tier ("party") and is stamped with portal_receipt proof.
      </li>
      <li>
        vCon lifecycle ledger (per draft-howe-vcon-lifecycle): an append-only,
        SHA-256 hash-chained audit trail of every vCon event — created by the
        recorder, received via upload, enhanced, sent to an external
        application, deleted, expired, redacted, purged by a receiver — with
        actor identity and source IP. Any modification of past entries is
        detectable by chain verification, and entries persist after the vCon
        itself is deleted. Bulk removal of entries (e.g. purging load-test
        residue) records a <em>ledger compacted</em> event in the rebuilt
        chain, so a rewritten stretch is disclosed rather than merely
        verifying clean.
      </li>
      <li>
        Retention &amp; expiry: per-domain retention policy (global default
        365 days) enforced by a disposition job — daily systemd timer on
        Linux, on-demand from the console — that expires vCons past
        retention (strictest party-domain rule wins), hard-deletes the
        document and optionally its WAV, ledgers vcon_expired as the
        tombstone, and auto-revokes time-limited consent grants whose
        expires_at has passed.
      </li>
      <li>
        Per-party redaction (vcon_party_redacted): replaces the party's
        name/number with a marker in the vCon and its embedded SIPREC
        metadata, silences their channel of the stereo audio in both the
        vCon and the source WAV (A-law, µ-law, and PCM), and preserves the
        conversation, its other parties, consent state, and the full audit
        trail — redaction is not deletion.
      </li>
      <li>
        Revocation cascade (RevokeRequestProcessing): consent revocation
        automatically serves a purge notice to every application that
        downloaded the vCon (holders are derived from the ledger's
        vcon_sent events); apps poll list_revocations and confirm with
        acknowledge_purge, ledgered as vcon_rcvr_purged and shown per-app
        in the console's copies &amp; notifications view.
      </li>
    </ul>

    <h4>Integration</h4>
    <ul>
      <li>
        Embedded pages in NetSapiens Horizon (Federation SDK / Module
        Federation): a SIPREC console the platform's own users reach from
        inside Horizon, with <em>Recordings</em> (search, in-page playback,
        the transcript read in place, and the vCon document itself)
        and <em>Consent</em> (per-purpose, with a signed receipt identical to
        the portal's). The Consent page shows the evidence behind each
        decision, not just its state: how the consent was acquired, its
        proof reference, and the latest signed receipt — which can be opened
        or re-verified in place, since the Ed25519 check needs only the
        receipt, its signature and the public key, all of which the public
        verification endpoint returns to anyone who asks. The same evidence is
        reachable from Horizon's own <em>Users</em> list, one row at a time —
        and a person with no record at all can be sent a consent request from
        there, which is the only thing that view writes.
        A user who can see only their own record is not shown a one-row
        table: they get an agreement-led view stating in words what they
        currently allow and what they have declined, and any change goes
        through the full agreement text — the same text, version and signed
        receipt the data-subject portal uses, including the choice of whether
        the decision reaches conversations already recorded.
        A transcribed call can be read without leaving the list: the row
        expands into the transcript, attributed to the party who spoke,
        labelled as machine output and carrying the recogniser's confidence —
        which for 8 kHz telephony is the difference between a quotation and a
        guess. Reading is gated on the same permission as listening, since a
        transcript reveals what the audio reveals, and a uuid from elsewhere is
        re-checked against the caller's scope rather than trusted. A recording
        with no transcript says why rather than showing nothing: not attempted,
        queued, refused for want of consent, no speech recognised, or failed —
        and, for anyone holding the permission, offers to transcribe it there
        and then. That runs the same code as the console and the nightly job:
        the same consent gate first, the same recognition, the same write-back
        and the same ledger entry, credited to the person who asked. It is a
        <em>separate</em> permission from playback, because starting
        transcription is not a read — it spends money at the recogniser,
        rewrites the stored conversation and appends to its lifecycle ledger, so
        a role trusted to listen to a call is not automatically trusted to bill
        for recognising it. A Super User grants it per role on the Access page,
        and it is off by default.
        The stored vCon behind a row can also be read in place, abbreviated the
        same way the console's View popup abbreviates it — a recorded call
        carries its audio inline as base64, so a 7.7 MB document renders as a
        2.5 KB one with each shortened body marked and its true length stated.
        The full document is deliberately not offered inside Horizon: it is
        megabytes of base64 to render as text, and the console already serves
        anyone doing forensics.
        Recordings also reach Horizon's <em>own</em> Call Logs page, through the
        host's <code>table-row-actions</code> extension zone — its shared data
        table renders that zone at the end of every row, so this needs no
        change to a page the app neither owns nor built. The correlation is the call's own
        SIP Call-ID: the SBC copies a session's <code>orig_callid</code> into
        the group id of the SIPREC metadata, the recorder stores it on the
        recording session, and it reaches the vCon by the recording's WAV
        basename — the one value joining a CDR to a conversation record, since
        neither carries the other's identifier. A call log row does not carry
        it, though: the CDR view behind the list has no such column, and the row
        is identified by its CDR id alone. It is therefore resolved a page at a
        time — the same window, re-requested at a limit of a hundred, comes back
        with each row's CDR id and Call-ID together — so a screen of call logs
        costs one request to the platform's API and one to this one, rather than
        a pair per row. A single row can front several recordings,
        because a transfer ends one and starts the next under the same group id
        with the following <code>group_seq</code>: with one the buttons act on
        it directly, with more they open the segments in order, each with its
        parties, duration and size. What exists is resolved server-side under
        the caller's own role — a row whose recording is not visible to them
        carries no buttons — and a whole page of call logs is answered in one
        batched request rather than one per row, since a button must know
        whether anything exists before it draws itself. Registered as two
        extensions rather than one drawing two buttons, because the host's
        action column reserves its width per registered extension.
        The same zone carries consent onto Horizon's <em>Users</em> page, where
        the join is the seat itself: a NetSapiens user is
        <code>user@domain</code>, which is exactly what the recorder stores as
        the party's uid, derived from the SIP address it saw. A seat whose
        account name and extension differ is asked about under both spellings,
        since either could have built that address. It is keyed on the person
        rather than a handset, so one decision covers every device they answer
        on, and — like the call-log buttons — a page of users costs one batched
        request, with rows the caller may not see coming back empty rather than
        refused, which is also what keeps the answer from being an oracle for
        who has been recorded. That popup reads and never writes: it reports
        the purposes, the basis, the proof and the latest receipt, and offers
        the same in-place verification, while every change stays on the Consent
        page that shows the agreement and issues the receipt. The single
        exception is a consent <em>request</em>, and it is deliberately not a
        way to record consent: it mints a tokenised portal invite, so the
        decision is made by the data subject and stored at the strongest
        evidence tier the platform has. The offer defaults to the domain's
        standing policy rather than to everything, since that is the position
        the operator has already taken for that customer. A live request is
        returned rather than duplicated — two working links for one person are
        not twice the chance of an answer, they are two links and a cancel that
        only stops one — and the request is filed under the identifier built
        from the SIP address, which is the one the recorder will later create
        the record under. Delivery is out of band, as it always has been here:
        the platform mints the link and emails nobody, and the origin to put in
        front of it is per-host configuration, because an invite token is a row
        in one database and only the portal reading that database can redeem
        it.
                The identity carries the Horizon instance that asserted it, not just
        the user ID: a NetSapiens user ID is unique within one instance and
        nowhere else, so two clusters can hold the same one and mean different
        people. The cluster arrives inside the signed webhook body, is stored
        with the token, and is shown on the app's Diagnostics page beside what
        Horizon handed the browser — the two disagreeing is the first thing
        worth seeing when a page shows the wrong person's records. Where one
        user ID is seen live from two clusters, the platform says so in its log
        rather than resolving it silently.
        Identity is never taken from the browser — Horizon's
        backend delivers it on an HMAC-signed webhook, this platform redeems
        the PKCE code against the NetSapiens API to read the caller's verified
        role, and issues its own short-lived token. What each page shows is
        decided in SQL here, not in the page: Basic User and Call Center Agent
        see only themselves (the pages read <em>My Recordings</em> /
        <em>My Consent</em>), Office Manager and Call Center Supervisor their
        domain, Reseller their territory, Super User the whole platform. A
        Super User gets a third tab, <em>Access</em>, that raises or lowers
        every other role's reach — including whether they may play audio or
        change consent — without a deploy, and refuses to let them lock
        themselves out of it.
        Both pages sit where the person using them would look for them, as
        real menu entries directly after <em>Call Logs</em>: under
        <em>Manage</em> for the roles that administer a domain or territory,
        and under <em>My Account</em> for everyone else, whose Recordings and
        Consent are their own.
      </li>
      <li>
        The embedded bundle is <strong>verified before Horizon will load
        it</strong>. The platform fetches the app's entry point, every chunk
        and every source map, hashes them, runs a content analyser and pins a
        SHA-384 of the exact bytes it judged; anything it rejects — or has
        never seen — is filtered out of the app list before the browser is
        told about it. The console is built for that: Subresource Integrity
        values on every chunk, so pinning the entry covers the code the entry
        loads rather than just the loader, and full source maps, so a finding
        can be attributed to a file and a line instead of to minified output.
      </li>
      <li>
        OAuth 2.1 sign-in for the MCP server, so a claude.ai custom connector
        (and with it Claude on iPad, web and desktop chat) or Claude Code
        without a key can connect: protected-resource and authorization-server
        discovery (RFC 9728 / RFC 8414), open dynamic client registration
        (RFC 7591; https or loopback redirect URIs only, rate-limited per IP,
        unused clients pruned after a week), authorization code with mandatory
        PKCE S256, single-use ten-minute codes, one-hour access tokens and
        thirty-day refresh tokens rotated on every use, where a replayed code
        or refresh token revokes everything descended from that approval.
        Sign-in uses the console's own password check, rate limits and
        per-account IP allowlist. A token carries no privileges of its own: the
        person chooses one of the MCP apps an administrator has opened to
        sign-in whose territory and domain lie inside their own, and acts with
        that app's privileges and scope, logged under the app and their name.
        The app, the setting and the person are re-checked on every request,
        so unticking Allow sign-in, disabling the app, or narrowing or
        deactivating the account disconnects at once. Only hashes of codes and
        tokens are stored. A one-line installer
        (<span className="monospace">connect_claude.php</span>) registers the
        server in Claude Code with no secret in it.
      </li>
      <li>
        MCP server (Model Context Protocol, Streamable HTTP): AI agents and
        external applications can query vCon metadata by UUID, parties, or
        time range; download vCons with per-part projection — choosing
        independently whether to inline the audio, the text dialog, or the
        attachments, so an email body or transcript comes back without the
        audio and without the bulky RFC822 headers, and the files attached to
        an email are listed by name, size and digest unless their bytes are
        asked for (<span className="monospace">include_files</span>); extract just the decoded
        dialog text — the email body or transcript, no base64, no audio, no
        headers — with the get_dialog_text tool when the whole document is not
        needed; read the text of the files attached to it, a page or slide
        range at a time, with get_attachment_text, which also names other
        conversations carrying the identical file so a deck forwarded several
        times is read once; see a page of an attached PDF, or an attached
        photo or diagram, as a picture with get_attachment_image, for what text
        cannot carry: charts, slide layouts, photos and scanned pages; upload new
        vCons; update existing ones; read and assert consent (status, grant
        with optional expiry, revoke); poll and settle revocation purge
        notices (list_revocations / acknowledge_purge); and read a vCon's
        lifecycle audit trail — governed by the per-app keys and privileges
        above. Every download is itself ledgered, so which application holds
        a copy of which conversation is always on record, and
        consent-revoked vCons are refused automatically. Every key is scoped to
        a territory and a domain, and query_vcons takes the same two arguments
        to narrow within that scope. See
        <span className="monospace"> MCP_GUIDE.md</span> and
        the <span className="monospace">vcon-mcp-demo</span> reference client.
      </li>
      <li>
        Full-text search over what was said: transcripts and email bodies are
        extracted into a searchable index as each vCon is stored, so a question
        like "which conversations mention cancelling a policy" is one query
        rather than a scan of every document in the store. It matches whole
        words rather than substrings, understands "exact phrases", +required
        and -excluded terms, orders results by relevance instead of date, and
        returns each hit with an excerpt showing the match in context. The
        files attached to an email are searched too: the text of a PDF,
        PowerPoint, Word or Excel file is extracted once when the conversation
        is stored, so a subject that appears only in an attached deck still
        finds the conversation, its excerpt naming the file. Search results
        list each conversation's files. The Ask tab sees each file's pages and
        opening when it reads a conversation, then reads further into the one
        that matters: a page range, or just the pages that contain the words it
        is looking for, so a figure on page 33 of a 40-page plan is found rather
        than cut off. A partial read names the pages it left out, so an answer
        never claims a file lacks something it simply did not read. Images, charts saved as pictures and older Office
        formats are listed but have no text to search; an application can
        still look at a PDF page or an image through the MCP server, sized to
        1568 pixels on its long edge. One
        limit is worth knowing: words shorter than three characters and very
        common words are never indexed, so a search for them legitimately
        returns nothing.
      </li>
      <li>
        Transcription, under consent: call recordings are transcribed by Google
        Cloud Speech-to-Text and the result written back into the vCon as
        dialog, which is what makes a phone call searchable alongside an email.
        Transcription is a <em>separate consent purpose</em> here, and the
        system treats it that way — a recording is never sent to a speech
        engine unless every party on the call has granted that purpose
        specifically, checked before the audio is so much as read. A call
        skipped for that reason is shown as skipped, naming the party, rather
        than quietly missing. Consent is followed in both directions: granted
        later, the call is transcribed on the next nightly run; withdrawn
        later, the transcript is removed from the vCon and the search index,
        because a revocation that left the text searchable would be cosmetic.
        SIPREC records each participant on their own channel, so each is
        recognised separately and every line is attributed to the person who
        said it — no guessing at who was speaking. A recording can also be
        transcribed on demand from the vCons page rather than waiting for the
        nightly run, and the Logs page reports usage: how much of the archive is
        transcribed, how much waits on consent and on whose decision, the
        recognition-confidence spread, and the time actually billed — taken from
        what the API reported charging rather than estimated from audio length,
        because per-request rounding and per-channel requests make those two
        genuinely different numbers.
      </li>
      <li>
        Local speech recognition: a host can transcribe with a whisper.cpp
        server running on the machine itself instead of with Google, so call
        audio never leaves it — not to a speech API, and not to Cloud Storage
        for staging long recordings either. The choice is made per host
        (STT_ENGINE), and everything else is unchanged: the same consent gate
        before any audio is read, the same per-channel recognition and
        attribution, the same write-back, index and ledger entry. Each
        transcript records which engine and model produced it, so calls done
        by either engine remain distinguishable. Confidence under whisper is
        the mean word probability, on the same 0–1 scale; billed time is
        zero, because nothing is billed. The Status page shows whether the
        whisper server is running and answering, its process, and the last
        day's transcriptions; the History page charts its availability and the
        transcriptions completed. Those samples are taken once a minute by a job
        of their own rather than by the recorder's heartbeat, because the
        whisper server keeps working while the recorder is stopped, and its
        history should too.
      </li>
      <li>
        Automatic transcription on a local engine: where speech recognition
        runs on the host itself, each call is transcribed within moments of
        being recorded instead of waiting for a nightly run. A launchd agent
        wakes when a vCon is written, and every 15 minutes regardless — which is
        also when it removes transcripts whose consent has been withdrawn,
        re-checks calls that were held for consent, and retries any that
        failed. It is the same job with the same consent gate, checked before
        any audio is read, so automatic means sooner and never more. It is
        offered only for a local engine: done with a cloud API it would send
        every new recording out, at a cost, without anyone deciding to. The
        vCons and Logs pages report what is actually observed running — "transcribing
        shortly" while the agent is running, and a warning if it
        stops.
      </li>
      <li>
        Time-aligned transcripts: SIPREC delivers each participant as their own
        stream, and the recording keeps them that way — one audio channel per
        participant, never mixed, lined up on a common clock. Each channel is
        recognised on its own, and every utterance is written into the vCon as
        its own entry carrying who said it, when it began and how long it ran.
        The Transcript view reads them back as the conversation: all
        participants interleaved in the order they spoke, each in their own
        colour with a timestamp, a speaker's run of lines joined into one turn,
        and speech that began while someone else was still talking marked as
        overlapping. With the local engine the times come from whisper's
        segments rather than its words, because with voice detection on only
        the segment times are on the recording's own timeline. Transcripts made
        before this carry one block per speaker and are shown as they are until
        they are re-run.
      </li>
      <li>
        Transcription language, per domain: recognition is <em>told</em> what
        language to expect — it does not work it out, and Google's automatic
        detection is documented for voice commands and searches rather than for
        phone calls. Asked for the wrong language the engine does not fail: it
        returns the few words of the wrong language it can force-fit, at low
        confidence, which reads as a broken transcriber rather than as the wrong
        question having been asked. So each domain carries the language its
        calls are in, used by every run on that domain, and a single recording
        can be recognised in another language straight from the vCons page — for
        the call that is not in the language its switch usually carries. The
        recognition model follows from the language rather than being fixed:
        <span className="monospace"> phone_call</span> is trained on the 8 kHz
        telephony this recorder captures, but it does not exist for every
        language — Cantonese and Mandarin have no telephony model at all — and
        asking for one a language does not have is not an error anybody sees,
        merely a substitution. Where a language does have more than one usable
        model the choice is offered per domain, and only ever among the models
        that language actually supports; a stored choice the new language
        cannot use is dropped, and said to be dropped. The language used is
        recorded against the call and shown beside its confidence, because a
        poor score means one thing in the language that was spoken and quite
        another in somebody else's. Languages are set under
        Logs → Transcription → Languages, beside the confidence figures that
        reveal a wrong one, and each domain's effective language appears
        read-only on Territories &amp; Domains — the view where noticing it is
        the point.
      </li>
      <li>
        Scheduled work says whether it is actually scheduled. The nightly
        disposition and transcription run is a systemd timer that only some
        deployments install, so the console used to promise every untranscribed
        recording was "queued for tonight" on hosts where tonight never came.
        The jobs now stamp each run, and the console reports what has been
        observed: a run that has never happened here is not a queue, and one
        that used to happen and has not lately is reported as stopped — the one
        failure a daily timer otherwise signals by staying quiet.
      </li>
      <li>
        MCP access log: every tool call is recorded with the search criteria it
        used, the scope it resolved to, how many rows matched before the limit
        against how many were returned, and the time split between querying the
        index and reading the store. The console groups those calls by query
        <em> shape</em> — the tool plus the set of filters used — and ranks the
        shapes by how much work each one throws away, which is the measurement
        the smarter-indexing work is built on: a search matching forty thousand
        rows to return twenty-five earns an index, one matching twenty-six does
        not. Search parameters can carry the names and numbers of recorded
        parties, so they are visible to Super Users only and the log ages out
        after ninety days.
      </li>
      <li>
        Email import from Google Workspace: a Gmail thread becomes a vCon of
        the same shape as a recorded call — the union of From/To/Cc as parties,
        one dialog entry per message, and each message's original RFC822
        headers retained as the provenance record, the exact role rs-metadata
        plays on a call. It is therefore the same object to everything
        downstream: the index, the lifecycle ledger, retention, consent, and
        MCP. Files attached to the mail travel inside the vCon, base64url
        encoded beside their name, size and SHA-256, so the conversation is
        self-contained and deleting or erasing it takes its files with it.
        Each vCon carries up to 32&nbsp;MB of them
        (<span className="monospace">EMAIL_ATTACHMENT_MAX_BYTES</span>); a
        file past that is still listed with its size and digest and marked
        as not carried. Listing stays cheap however large they are, because
        the files are stored after the part a listing reads. Mail stored
        before files were carried lists them by name, size and digest only.
        A part that arrives with no filename is named from its own
        content digest, which makes the same file appearing twice in a
        forwarded thread visibly the same file. Deduplication keys on the
        RFC822 <span className="monospace">Message-ID</span>, not Gmail's
        thread id, because the latter is per-mailbox — an internal ten-person
        thread would otherwise import ten times.
      </li>
      <li>
        Consent for imported mail is not assumed, and the import refuses by
        default rather than presuming. A thread is imported only when every
        party is internal; anything involving an external correspondent is
        declined unless explicitly overridden, so forgetting a flag imports
        nothing rather than importing a third party's mail. Correspondents are
        discovered separately, from message headers alone — never a message
        body — and registered as known parties with no consent to any purpose,
        classified as a person, a shared mailbox or an automated sender, since
        only a person can be asked. Nothing about being discovered grants
        anything.
      </li>
      <li>
        Forwarded email: a colleague points an auto-forward rule in their own
        mailbox at the receiver address, and each message arrives as its own
        vCon, threaded back together with its replies through the References
        header. The receiver handles outside parties the same way as chat. A
        message with anyone outside the organisation is held and nothing is
        written until <span className="monospace">email_outside_parties</span>{' '}
        is turned on. Held mail is not lost, because it is still in the
        colleague's mailbox. Before that gate, the message has to be verifiably
        ours, since anyone can send mail to an address and put any name in
        From. Cloudflare must have authenticated the forwarding mailbox as one
        of our own, and Google must have authenticated the original sender
        when the message reached that mailbox. Only the first verdict each of
        them added is believed, so a sender cannot plant one of their own.
        The same message forwarded by two colleagues is stored once.
        One recorder can receive for several organisations: each has its own
        receiver address and its own key, and the key decides both where the
        mail is filed (its territory and domain) and whose mail it is. The
        Worker knows none of this. It relays every message with one key of its
        own and the address it was sent to, and the receiver key holding that
        address takes over, so a new organisation is a single form on the MCP
        Apps page and nothing changes in Cloudflare. So
        "the organisation" above means that key's own domains. A message to
        another organisation's address is judged by that organisation's
        domains, and our own colleagues count as outside parties there, and
        the other way around.
      </li>
      <li>
        Chat: the chat portal's archive module posts each finished burst of
        an archived chat — closed after six hours of silence, or 24 hours
        after its first message, whichever comes first — to
        chat_ingest.php, with an API key that holds a chat-ingest capability
        of its own and is scoped to one territory and domain, under which the
        record is filed. One text dialog per message at its original time,
        every human member a party with a matrix: URI under a declared
        "matrix" extension and the person's uuid, the room as the CC
        extension's interaction_id so every burst of a chat is one thread,
        and a matrix-metadata attachment as provenance. Participants are
        registered like discovered correspondents, with no consent to any
        purpose, and a discovered-only registry row never counts as a
        refusal when a person's consent is combined. A burst with anyone who
        is not an account on the posting homeserver is answered "held" and
        nothing is written while the platform setting for outside parties
        (chat_outside_parties) is off; it stays off until the lawful basis for
        third parties' messages is settled. Set to consent, it decides
        contact by contact (chat_consent_common.php): a burst is stored when
        every outside contact has granted recording on the consent portal and
        proved it, keeping only the messages from the time their consent
        covers; answered 410 and deleted by the module when any of them
        declined or let the request expire; held otherwise. The request is
        made through chat_consent_request.php (the archive module's key; a
        portal invite per contact offering recording and analysis, nothing
        pre-ticked, 7 days, the contact's matrix: address,
        the newest replacing any unanswered one, optionally covering the
        messages since the chat was archived) and sent into the chat by the
        module as the person whose chat it is. A grant of recording on such
        an invite returns a single-use code (only its hash is kept); the
        module posts a message carrying it to chat_consent_confirm.php, which
        accepts it only from the contact the invite was for and raises their
        chat identifier to PROVEN (proof chat_reply_code) with an
        identifier_proven ledger entry, and signs a proof receipt (consent_receipts
        action identity_proof, proves_receipt_id = the consent receipt): the chat
        address and network, the room, the message's event id, when it arrived
        and the code's hash as kept (sha256 of "siprec-chat-reply-code:" + code),
        never the code. The verification endpoint names a consent receipt's
        proofs (proven_by, outside the signed bytes); chat_consent_proof_backfill.php
        signed them for consents proven before 3 Nov 2026. A contact already proven is not asked
        for a code again, and a grant after a refusal counts only from that
        grant. That is the bar a chat decision must
        meet, so once proven the person's own decision governs, including a
        later withdrawal. The receipt names the person and which messages
        the consent covers. A bridged contact may carry same_as, naming an
        account on the homeserver it is proven to be (source bridge_login,
        or admin with a reason of at least eight characters); it then counts
        as internal, its identifier is joined to that account's person and
        raised to PROVEN or ADMIN with an identifier_proven ledger entry. A
        link that would merge two people who each hold other identifiers is
        refused and named in the "held" answer. Idempotent per burst, so a
        resend finds the stored record.
      </li>
      <li>
        Portable data formats: standard WAV audio and vCon JSON — no
        proprietary formats, no lock-in.
      </li>
    </ul>

    <h4>Asking questions in plain English</h4>
    <ul>
      <li>
        A question is answered by a language model that cannot reach the
        database. It is given six read-only functions — describe the scope,
        search, count, read one conversation, list parties, check consent —
        and composes them; there is no tool that writes, uploads, deletes or
        alters consent, which is a security property rather than an omission,
        because call transcripts are untrusted input and a caller who says
        "ignore your previous instructions" is text this system will one day
        put in a prompt. Scope is not something the model can influence: the
        territory and domain are resolved once from the asking account, and
        every tool call is re-intersected against them by the same function
        that decides access on every other page. A model that asks to look
        wider gets a refusal it can read and explain, and no rows.
      </li>
      <li>
        What leaves the platform, exactly: the text of a conversation, sent to
        Google Vertex AI in the same Google Cloud project and region that
        already holds the recordings and performs the speech recognition. No
        audio is ever sent. Metadata — counts, dates, party names already in
        the index — is not gated, but a conversation's words are: every party
        must have granted the <span className="monospace">analysis</span>{' '}
        purpose, which is separate from{' '}
        <span className="monospace">transcription</span> and not implied by it.
        A revocation blocks it outright and cannot be overridden. A Data
        Controller may proceed past a missing grant deliberately, and that
        override is recorded as such against the conversation rather than
        leaving an ordinary-looking read. Every disclosure is a{' '}
        <span className="monospace">vcon_sent</span> ledger entry naming the
        processor and the model, so the inference provider appears on a
        conversation's holders list beside the applications that downloaded it.
      </li>
      <li>
        Or nothing leaves the host at all. Per host, the engine can be a local
        model served over the OpenAI-compatible chat API — Ollama, LM Studio,
        llama.cpp or vLLM on loopback — instead of Vertex AI. The tools, scope,
        consent gate and citation check are the same code on either engine; the
        ledger names the local server as the processor, the question is logged
        at a cost of zero and does not draw on the daily ceilings, and a prompt
        larger than the model's configured context is refused rather than
        silently truncated. Answers take minutes rather than seconds on a
        laptop. A comparison run replays logged questions through both engines
        and puts the answers, tool trails, times and costs side by side.
      </li>
      <li>
        Where a host offers both engines, the Ask tab lets the person asking
        choose per question — Gemini or any of the host's configured local
        models, each with its own settings — and the footer of every answer
        names the engine and model that wrote it. A local model is offered only
        while the server is running and has it; one that is listed but missing
        or stopped is named with the reason instead of offered. An engine the host does not
        list is refused, never quietly swapped for another. Applications asking
        through MCP cannot choose: deciding where conversation text is sent is
        the controller's call, so they always get the host's default.
      </li>
      <li>
        An answer shows its work. Every tool call is listed with its arguments
        — which is how you notice that a search ran against the wrong domain,
        or for a word the index cannot hold — alongside the turns taken, the
        conversations read, the tokens spent and what they cost. Citations are
        validated server-side against what the searches actually returned: a
        reference the model produced but no tool ever handed it is removed and
        the answer marked as partly ungrounded, because a plausible citation
        that leads nowhere is worse than a missing one.
      </li>
      <li>
        The feature knows what it cannot see, and says so. A search that finds
        nothing is a different answer depending on why: no conversation
        mentions the word, or the conversations have no searchable text at all
        — never transcribed, no audio, recognised into silence, or withheld on
        consent grounds. The model is told which before it answers, so a thin
        result is reported as a thin result rather than as an absence of
        business. Words shorter than three characters and common stopwords are
        not indexed, and that is stated too rather than left to look like a
        finding.
      </li>
      <li>
        Off by default at two independent levels, and metered. An account holds
        the permission or it does not — it is not granted to administrators by
        being administrators, because it spends money on every use. A territory
        is open to inference or it is not, which is a question about a body of
        recordings and their licence rather than about who is asking; a closed
        territory is <em>named</em> as excluded in the answer instead of
        quietly contributing nothing. Per-person and whole-platform daily
        ceilings refuse a question before a token is spent and say which
        ceiling was reached, with a Google Cloud budget alert beneath them as a
        smoke detector — it reports, it does not stop. Every question is logged
        with its cost, its tool sequence and what it disclosed, grouped under
        Logs → Ask by shape rather than by total, since the shape is the thing
        that can actually be fixed.
      </li>
      <li>
        Registered applications can ask too, through an{' '}
        <span className="monospace">ask_vcons</span> tool on the MCP server,
        under a privilege that is off by default on every key — including keys
        that already hold every other privilege, because this is the only one
        that spends money rather than granting access to data the key was
        registered to handle. A question is bounded by the key's own territory
        and domain, not by anything the caller asks for: naming a territory
        outside them is refused by name rather than quietly narrowed, before a
        token is spent. Each key is metered against its own daily allowance, so
        one integration cannot spend another's or a person's.
      </li>
      <li>
        An application is a <em>processor</em>, and the difference is enforced
        rather than described. A person in this console, acting as Data
        Controller, may deliberately read a conversation whose parties have not
        granted the analysis purpose, and answers for having done so — the
        override is recorded against that conversation as an override. An
        application cannot make that decision at all: the tool has no such
        argument, and one cannot be supplied. So the log records which surface
        each question came through, and the two are filterable apart, because
        what was permitted depends on which of them asked.
      </li>
    </ul>

    <h4>Architecture</h4>
    <p>
      Three cooperating components share one MariaDB session store: a C++
      recording engine (the SIPREC SRS and media pipeline), a PHP API layer
      (console endpoints and the MCP server, behind Apache), and this React
      console. The full platform runs on a single Linux VM or Mac, with the
      recorder supervised as a system service.
    </p>

    <h4>At a glance</h4>
    <div className="table-container">
      <table className="sessions-table">
        <tbody>
          <tr><td>Signaling</td><td>SIPREC (RFC 7865/7866); SIP over TCP and TLS</td></tr>
          <tr><td>Media</td><td>RTP / SRTP (SDES; AES-CM-128 and AES-CM-256 with HMAC-SHA1)</td></tr>
          <tr><td>Codecs</td><td>G.711 µ-law, G.711 A-law, G.722 (64/56/48 kbit/s), Opus; resolved per stream and per packet from the RTP payload type, mixed codecs across legs supported</td></tr>
          <tr><td>Recording output</td><td>WAV — µ-law, A-law, or linear PCM; mono or stereo; 8–48 kHz</td></tr>
          <tr><td>Conversation records</td><td>vCon (IETF draft) with inline base64url audio and rs-metadata attachment</td></tr>
          <tr><td>Session metadata</td><td>RFC 7865 rs-metadata, including NetSapiens extensions</td></tr>
          <tr><td>Session store</td><td>MariaDB</td></tr>
          <tr><td>vCon store</td><td>Google Cloud Storage, addressed by URI from the metadata index; local disk is a staging area drained on a timer</td></tr>
          <tr><td>Organisation</td><td>Two-level hierarchy: territory &gt; domain, the domain named for the owning party's host and derived at index time (alias table for exceptions); every vCon, session, consent record, account and API key scoped to it</td></tr>
          <tr><td>Access control</td><td>Three account levels (super / territory / domain) enforced server-side on every listing; requests outside an account's scope refused, not silently emptied; per-key MCP scope as a hard isolation boundary including UUID and filename lookups</td></tr>
          <tr><td>Programmatic access</td><td>MCP 2025-06-18 (JSON-RPC 2.0 over Streamable HTTP, Bearer API keys or OAuth 2.1 sign-in with PKCE and dynamic client registration for claude.ai connectors, per-key territory/domain scope enforced as an isolation boundary including uuid and filename lookups, optional per-key source-IP allowlist); thirteen tools across query, download, upload, update, consent, lifecycle and natural-language ask, each behind its own privilege and advertised only to keys that hold it</td></tr>
          <tr><td>Transcription</td><td>Google Cloud Speech-to-Text over the REST API, or per host a local whisper.cpp server (large-v3-turbo, Silero VAD, loopback only); language set per domain and overridable per recording, with the model following the language (enhanced phone_call, telephony, or the general model for languages having neither); per-channel recognition for speaker attribution; gated on the per-party transcription consent purpose, swept in both directions nightly; written back as vCon dialog and ledgered as vcon_enhanced</td></tr>
          <tr><td>Content search</td><td>Extracted dialog text in a MariaDB InnoDB FULLTEXT index (natural-language relevance, or boolean mode for phrases/operators); text extracted at index time, ~6% of document bytes; snippets generated per result; audio-only recordings hold no text. Attached files' text in a second FULLTEXT index (vcon_attachment_text, one row per file, rewritten whenever the vCon is re-indexed so a redaction removes it): PDF via pdftotext, pptx/docx/xlsx via ZipArchive, text and HTML directly, pages/slides/sheets separated by form feeds; a file already extracted for another vCon (same SHA-256) is reused; queried separately from the dialog index and merged, so a search with no file hits costs what it did before. Pictures on request (MCP get_attachment_image): a PDF page rendered by pdftoppm, or a PNG/JPEG/GIF/WebP file, at most 1568 px on the long edge and 3.75 MB (PNG, JPEG when a page is too large; images scaled with GD), consent-gated and ledgered like a text read</td></tr>
          <tr><td>Natural-language querying</td><td>Gemini on Vertex AI (same GCP project and region as the vCon store and speech recognition), or per host a local model over the OpenAI-compatible chat API (Ollama by default, loopback only, ledgered as a local processor, unmetered, context overrun refused), selectable per question in the console, including between several local models with per-model settings, where a host offers them, but never by an MCP application; driven server-side through six read-only tools; scope re-intersected per tool call, never model-supplied; reading a conversation's text gated on the per-party analysis consent purpose and ledgered as vcon_sent naming the processor; citations validated against tool output; per-account permission and per-territory enablement both default off; per-user and platform daily token ceilings; server-sent-event streaming of the tool trail; per-question cost log grouped by tool sequence, 90-day retention. Also exposed to registered applications as the MCP tool ask_vcons under its own per-key privilege (default off), bounded by the key's territory/domain and metered against its own allowance; the Data Controller's consent override exists only in the console, never for an application, and the log records and filters by which surface asked</td></tr>
          <tr><td>Access measurement</td><td>Per-call MCP log: search criteria, resolved scope, matched-vs-returned selectivity, query/storage timing split and index path; aggregated by query shape to rank indexing candidates; 90-day retention</td></tr>
          <tr><td>Lifecycle audit</td><td>Append-only SHA-256 hash-chained ledger, draft-howe-vcon-lifecycle event model, on-demand chain verification</td></tr>
          <tr><td>Consent</td><td>draft-howe-vcon-lawful-basis attachment model (purpose "lawful_basis", JSON body carrying the basis, expiry, per-purpose grants and proof mechanisms), written alongside the expired draft-howe-vcon-consent shape in one attachment and read from either, so stored documents and outside implementations both resolve; per party × purpose (recording, transcription, analysis, ai_training); lawful basis held per party, per domain and per grant across all six GDPR Article 6 grounds, so parties to one conversation may rest on different ones; registry + per-domain defaults (basis and evidence inherited together) stamped at creation; typed proof-of-acquisition evidence, distinct from the basis it evidences; revocation recorded as an explicit per-purpose denial, enforced at the MCP boundary, and cascaded to destroy data derived under the withdrawn purpose. The absence rule is per basis and per caller: under consent, no grant is a refusal at the MCP boundary (draft §6.4 rule 4) while the console as Data Controller warns instead; under every other basis absence permits, since legitimate interest is opt-out and contract and legal obligation hold their authority outside a grant row. A conversation with no indexed parties — an imported corpus with no participants — is exempt rather than denied, and reported as such, since consent is a relationship with a data subject and there is none to ask. Each purpose names the code that enforces it: recording at MCP download and console playback, transcription before any audio is read, analysis before conversation text enters a prompt or is scored. ai_training is recorded, offered and revocable but enforced nowhere, there being no training pipeline and no bulk-content export that is not already a disclosure gated on recording — stated here rather than left to be assumed, since a purpose offered on a receipt and consulted by no code tells a data subject something untrue</td></tr>
          <tr><td>Consent receipts</td><td>Data-subject portal (tokenized passwordless links, raisable per person from the console's consent-requests page or from a row of Horizon's own Users list) and the Horizon-embedded Consent page; Ed25519-signed JLINC/SISA-aligned receipts naming the lawful basis alongside the purposes, with a public verification endpoint that answers a person with a readable receipt page and a program with JSON; a person answering for themselves sets the basis to consent whatever the operator had asserted, since that is the one ground they can later withdraw</td></tr>
          <tr><td>Chat</td><td>Matrix homeserver archive module (visible recorder member, bursts closed after 6 h of silence or 24 h after their first message) posting to chat_ingest.php with a chat-ingest-only API key; one text dialog per message; parties carry a matrix: URI (MSC2312 user form) under a declared "matrix" extension and the person uuid; room as CC interaction_id; matrix-metadata provenance attachment; internal-only while platform_settings.chat_outside_parties is off ('on' admits all; 'consent' admits a burst when every outside contact granted on the consent portal and proved it by sending the portal's single-use code back from their own chat account, PROVEN by chat_reply_code, messages limited to what the consent covers; a decline or an expired 7-day request answers 410 and the burst is deleted), except a bridged contact proven to be one of our accounts (same_as: own bridge login, or an admin link with a reason), filed under that person at PROVEN or ADMIN assurance with a ledger entry; idempotent per burst</td></tr>
          <tr><td>Forwarded email</td><td>Mailbox auto-forward to a Cloudflare Email Routing address; an Email Worker posts the raw RFC822 message and its envelope sender to email_ingest.php with an email-ingest-only API key; one vCon per message, uuid derived from Message-ID (a second colleague's copy is a duplicate), threaded by the References root; attached files carried inline as type "file" attachments (base64url body, filename, mimetype, size_bytes, sha256) up to EMAIL_ATTACHMENT_MAX_BYTES per vCon (default 32 MB), beyond which a file stays a "file-reference" marked not_carried; accepted only when the first Authentication-Results from Cloudflare shows SPF pass for one of our own mailboxes as the forwarder, and the first from Google shows the From domain aligned by SPF, DKIM or DMARC; "internal" is per key (mcp_apps.internal_domains, else the key's scope_domain), so each organisation's receiver address and key judges its own forwarders, outside parties and internal-mail evidence while the platform-wide EMAIL_INTERNAL_DOMAINS stays as it is; the Worker relays every message with one relay-only key (mcp_apps.can_relay_email, refused any other privilege) plus X-Envelope-To, and email_ingest.php files it under the receiver key whose mcp_apps.receiver_address matches (unique), answering 404 and recording nothing for an address no active receiver holds; receiver keys (chat or email ingest, receiver address, internal mail domains, a not-yet-existing territory and domain) and the relay key are made on the MCP Apps settings page, which clears the read privileges when a receive capability is ticked, which refuses an ingest capability on a key scoped to "all"; internal-only until platform_settings.email_outside_parties is turned on; Gmail forwarding confirmations routed to a person, never stored; a metadata-only email_ingest_log row per message (no subject, body or correspondent addresses)</td></tr>
          <tr><td>Identity</td><td>One person per human or shared line, owning typed identifiers (ns_uid, sip, E.164 tel, mailto, matrix — read from a vCon party's matrix: URI —, WhatsApp JID and LID, Messenger, Google Chat) with assurance levels and validity windows; exact normalised matching held to one vector file across PHP and C++; core §4.2.10 party uuid carries the person id, uid the NetSapiens account; proof by Horizon sign-in or a one-time code by email or chat (own homeserver only); phone-number matches suggested, never joined; merge (most restrictive consent wins), split and rename by territory administrators with a reason, or by the person after proving both addresses; shared identifiers never carry a personal grant; hash-chained person ledger; consent at capture decided by registry or person under a per-channel assurance bar, switchable platform-wide</td></tr>
          <tr><td>Embedded UI</td><td>NetSapiens Horizon federated app (Webpack Module Federation); brokered remote auth (webhook signature v2 — HMAC over the raw body, header-borne — plus PKCE code redemption, with the asserting cluster recorded alongside the identity since a user ID is unique only within one Horizon instance); role-scoped visibility enforced server-side, editable per role by a Super User, including a transcribe-on-demand permission held separately from playback; Recordings and Consent placed after Call Logs under Manage or My Account by role; in-row transcript panel sharing one renderer with the console's transcript view, gated on the same permission as playback and re-scoped per uuid; vCon document popup sharing the console's abbreviation rules (bodies over threshold cut and marked body_truncated with body_total_chars); Play and vCon buttons on Horizon's own Call Logs rows via the table-row-actions zone, correlated by the call's SIP Call-ID (resolved from the row's CDR id, since the call-log view omits it) and batched one request per page of rows rather than per row; per-user consent on Horizon's own Users rows through the same zone, joined on the seat (user@domain, asked under both the account-name and extension spellings since either could have built the SIP address the recorder saw) and likewise batched per page — showing the purposes, basis, proof and signed receipt with in-place Ed25519 re-verification, and for a person with no record the one write that view permits: a tokenised portal invite offering the domain's standing policy, returned rather than duplicated while live and cancellable, so the decision is the data subject's own rather than an administrator's assertion about them; a My addresses panel in the self view, where a signed-in seat — proven by the sign-in itself — links its own email or chat account by one-time code and removes what it linked, so one consent decision covers every channel the person uses; bundle passes Horizon's verification gate (per-chunk SHA-384 Subresource Integrity plus source maps)</td></tr>
          <tr><td>Cross-channel threading</td><td>Conversations linked into an interaction via draft-ietf-vcon-cc-extension-02 §3.2.3 interaction_id, written onto every dialog entry with the CC extension declared, so the link travels inside the documents and not only in the index; core's own group parameter is registered "reserved for future extension" with no specification and is deliberately not used. Separate vCons rather than one merged document, since appending to a stored conversation means amend, new uuid, reindex and ledger entry per message. Two kinds of candidate, kept apart: relations the recording client asserted — segments of one SIPREC session (shared group_id), and a supervised transfer's consultation leg, which is a separate session reachable only through the xferredGroupID the post-transfer segment carries — are linked automatically, following that pointer in both directions to the connected component so that two consultations of one call also find each other; a blind transfer self-references and contributes no new session. Relations merely inferred — a shared participant identity (the registered party uuid, core §4.2.10) within a configurable window, ranked by shared parties then closeness in time — are suggested only, and linked when an operator confirms. A Threads page lists every interaction in scope, expandable to its conversations with per-member unlink, each carrying its SIPREC session, sequence within that session, the SBC's own byAction and the session it was transferred from — shown as a letter per session with a legend giving the full group ids, since a transfer chain is unreadable as six raw identifiers. thread_autolink.php applies the asserted links in batch and is idempotent. Thread-wide processing is gated separately from per-conversation processing and takes the most restrictive member's answer, so linking can never widen what was already permitted</td></tr>
          <tr><td>Disposition</td><td>Per-domain retention with hard-delete expiry (vcon_expired tombstones); per-party redaction with audio-channel silencing, in two modes — erase in place, irreversible, for a right-to-erasure request; or derive a new vCon carrying draft core §4.1.8's redacted object referencing the original by uuid, leaving it intact. Once a derivative exists the unredacted original is restricted at the MCP boundary as that section requires, and applications are answered with the derivative's uuid rather than simply refused; poll-based revocation cascade to copy holders (vcon_rcvr_purged), which also destroys data derived under a withdrawn purpose</td></tr>
          <tr><td>Runtime</td><td>C++17 recorder (systemd/manual), Apache + PHP 8 API, React console; Linux or macOS. Restart is claimed by the running recorder from a database flag on its own heartbeat; a cold start is a request file the API writes and a host watcher acts on — a systemd path unit activating a oneshot on Linux, a launchd WatchPaths agent on macOS — neither of them a supervisor, and neither taking any input from the request beyond the fact of it</td></tr>
        </tbody>
      </table>
    </div>
  </section>
);

const AboutPage: React.FC = () => {
  const [tab, setTab] = useState<'product' | 'specs'>('product');

  return (
    <div className="status-container about-container">
      <h2>About</h2>

      <nav className="settings-subnav">
        <button className={`nav-button ${tab === 'product' ? 'active' : ''}`} onClick={() => setTab('product')}>
          Product
        </button>
        <button className={`nav-button ${tab === 'specs' ? 'active' : ''}`} onClick={() => setTab('specs')}>
          Technical Specifications
        </button>
      </nav>

      {tab === 'product' && <ProductSection />}
      {tab === 'specs' && <SpecsSection />}

      <BuildStamp />
    </div>
  );
};

export default AboutPage;
